import {
  createStyles,
  Divider,
  fade,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
} from '@material-ui/core';
import clsx from 'clsx';
import React, { FC, forwardRef, Ref } from 'react';
import { ColumnInstance, Filters, HeaderGroup, Row, TablePropGetter, TableProps } from 'react-table';
import DataGridNoDataBody, { DataGridNoDataBodyProps } from '../DataGridNoDataBody';
import { VirtualizeOptions } from './DataGrid';
import {
  CustomTableBodyProps,
  CustomTableRowProps,
  TableBodyComponentContainer,
  TableBodyComponentProps,
  TableRowComponentProps,
} from './TableBodyComponentContainer';

type TableContainerWrapperProps<
  TRowData extends Record<string, unknown>,
  TBodyProps extends Record<string, unknown> = Record<string, unknown>
> = {
  virtualize?: boolean;
  displayNoColumnMessage: boolean;
  loading?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tableOptions?: { [key: string]: any };
  allColumns: ColumnInstance<TRowData>[];
  headerGroups: HeaderGroup<TRowData>[];
  rows: Row<TRowData>[];
  data?: TRowData[];
  onRowClick?: (row: Row<TRowData>) => void;
  prepareRow: (row: Row<TRowData>) => void;
  tableStateChangeListener: boolean;
  virtualizeOptions?: VirtualizeOptions;
  onScroll?: (args: {
    scrollDirection: 'forward' | 'backward';
    scrollOffset: number;
    scrollUpdateWasRequested: boolean;
  }) => void;
  TableBodyComponent?: ({ children }: CustomTableBodyProps & TableBodyComponentProps<TBodyProps>) => JSX.Element;
  TableBodyComponentProps?: TableBodyComponentProps<TBodyProps>;
  TableRowComponent?: ({ children }: CustomTableRowProps) => JSX.Element;
  TableRowComponentProps?: TableRowComponentProps;
  noDataComponent?: FC<DataGridNoDataBodyProps>;
  noDataComponentProps?: DataGridNoDataBodyProps;
  getTableProps: (propGetter?: TablePropGetter<TRowData> | undefined) => TableProps;
  setTableStateChangeListener: (value: boolean) => void;
  filters: Filters<TRowData>;
  globalFilter: string | undefined;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableContainer: {
      maxHeight: '80vh',
      overflow: 'auto',
    },
    overflow: {
      overflowY: 'hidden',
    },
    stickyHead: {
      backgroundColor: theme.palette.background.paper,
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
    headerSort: {
      position: 'relative',
      userSelect: 'none',
      '&:hover': {
        zIndex: 10,
        backgroundColor: fade(theme.palette.secondary.main, 0.12),
      },
    },
    removeLastSeparator: {
      fontSize: 14,
      '&:last-child $separator': {
        display: 'none',
      },
    },
    cell: {
      display: 'flex',
    },
    emptyMenuCell: {
      maxWidth: theme.spacing(6),
      padding: theme.spacing(0),
      justifyContent: 'center',
      display: 'flex',
      alignItems: 'center',
      '&:not(.MuiTableCell-sizeSmall)': {
        height: 'auto',
      },
    },
    nonResizeable: {
      display: 'flex',
      flex: '1 0 auto !important',
    },
    separator: {
      position: 'absolute',
      top: 0,
      height: '100%',
      right: theme.spacing(-0.9375),
      padding: theme.spacing(0.5, 1),
      zIndex: 1,
    },
    sepResizer: {
      zIndex: 1,
      '&:hover': {
        cursor: 'ew-resize',
        zIndex: 10,
        '& .MuiDivider-root': {
          backgroundColor: theme.palette.action.hover,
        },
      },
      '&:active, &:active:hover': {
        cursor: 'grabbing !important',
        padding: theme.spacing(0.5, 2),
        right: theme.spacing(-2),
        '& .MuiDivider-root': {
          backgroundColor: theme.palette.action.active,
        },
      },
    },
  }),
);

/**
 * Forwards ref to underlying mui Table div
 */
export const TableContainerWrapper = forwardRef(function Wrapper<
  TRowData extends Record<string, unknown>,
  TBodyProps extends Record<string, unknown> = Record<string, unknown>
>(
  {
    virtualize = true,
    displayNoColumnMessage,
    loading,
    tableOptions,
    allColumns,
    headerGroups,
    rows,
    data = [],
    onRowClick = () => undefined,
    prepareRow,
    tableStateChangeListener,
    virtualizeOptions,
    onScroll,
    TableBodyComponent = TableBody,
    TableBodyComponentProps,
    TableRowComponent = TableRow,
    TableRowComponentProps = { component: 'div' },
    noDataComponent = DataGridNoDataBody,
    noDataComponentProps = {},
    getTableProps,
    filters,
    globalFilter,
    setTableStateChangeListener,
  }: TableContainerWrapperProps<TRowData, TBodyProps>,
  ref: Ref<HTMLDivElement>,
) {
  const classes = useStyles();

  return (
    <TableContainer
      className={clsx(classes.tableContainer, virtualize && classes.overflow)}
      data-testid="table-container-wrapper"
    >
      <Table ref={ref} stickyHeader component="div" {...getTableProps()}>
        {!displayNoColumnMessage ? (
          <TableHead component="div" className={classes.stickyHead}>
            {headerGroups.map((headerGroup, headerIndex) => (
              <TableRow
                {...headerGroup.getHeaderGroupProps()}
                className={classes.stickyHead}
                key={headerIndex}
                component="div"
              >
                {headerGroup.headers.map((column, columnIndex) => {
                  //@ts-expect-error headerProps has bad type
                  // Prevents column ordering when resizing columns
                  const { onClick, style, ...headerProps } =
                    column.id === 'Selection'
                      ? column.getHeaderProps()
                      : column.getHeaderProps(column.getSortByToggleProps({ title: undefined }));
                  const isMenuColumn = column.Header == 'Menu';
                  if (loading && isMenuColumn) return undefined;

                  return (
                    <TableCell
                      className={clsx(
                        classes.removeLastSeparator,
                        isMenuColumn
                          ? (classes.emptyMenuCell, classes.nonResizeable)
                          : (classes.cell, classes.headerSort),
                        (column.disableResizing || tableOptions?.disableResizing == true) && classes.nonResizeable,
                      )}
                      style={{ ...style, pointerEvents: loading ? 'none' : 'auto' }}
                      {...headerProps}
                      component="div"
                      key={`header-${headerIndex}-column-${columnIndex}`}
                      data-testid={`header-${headerIndex}-column-${columnIndex}`}
                      onMouseUp={() => setTableStateChangeListener(!tableStateChangeListener)}
                    >
                      <div style={{ display: 'inline-flex' }} onClick={onClick}>
                        {!isMenuColumn && column.render('Header')}
                        {column.id !== 'Selection' && !isMenuColumn && column.canSort && (
                          <TableSortLabel
                            active={column.isSorted}
                            // react-table has a unsorted state which is not treated here
                            direction={column.isSortedDesc ? 'desc' : 'asc'}
                            data-testid={`sort-on-head-${headerIndex}-col-${columnIndex}`}
                          />
                        )}
                      </div>
                      {!loading && !isMenuColumn && column.canResize && (
                        <span {...column.getResizerProps()} className={clsx(classes.separator, classes.sepResizer)}>
                          <Divider orientation="vertical"></Divider>
                        </span>
                      )}
                      {(!column.canResize || loading) && (
                        <span className={classes.separator}>
                          <Divider orientation="vertical"></Divider>
                        </span>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
        ) : (
          <Divider orientation="horizontal" />
        )}
        <TableBodyComponentContainer
          loading={loading}
          allColumns={allColumns}
          tableOptions={tableOptions}
          headerGroups={headerGroups}
          displayNoColumnMessage={displayNoColumnMessage}
          rows={rows}
          virtualize={virtualize}
          data={data}
          onRowClick={onRowClick}
          prepareRow={prepareRow}
          tableStateChangeListener={tableStateChangeListener}
          virtualizeOptions={virtualizeOptions}
          onScroll={onScroll}
          TableBodyComponentProps={TableBodyComponentProps}
          TableBodyComponent={TableBodyComponent}
          TableRowComponent={TableRowComponent}
          TableRowComponentProps={TableRowComponentProps}
          NoDataComponent={noDataComponent}
          NoDataComponentProps={noDataComponentProps}
          hasActiveFilters={!!filters?.length || !!globalFilter?.length}
        />
      </Table>
    </TableContainer>
  );
});
